import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { ConfigStateService, getShortDateFormat, ListService, PagedResultDto } from '@abp/ng.core';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
} from '@angular/core';

import { DatatableComponent, SelectionType, ColumnMode } from '@swimlane/ngx-datatable';
import { ModelService } from '@proxy/register-service/models';
import type { AssetDetailsDto, GetAssetsInput } from '@/proxy/register-service/assets/models';
import { AssetService } from '@/proxy/register-service/assets/asset.service';
import { AssetType, ComplianceStatus } from '@proxy/register-service/assets';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { ColumnPreferenceService } from '@/assets/services/column-preference.service';
import { ColumnConfig } from '@proxy/settings/models';

type ColumnMetadata = { id: string } & ColumnConfig;

@Component({
  selector: 'app-assets-list',
  templateUrl: './assets-list.component.html',
  styleUrls: ['./assets-list.component.scss'],
})
export class AssetsListComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('assetsTable') table: DatatableComponent;

  @Input() assetIdForSubAssets = '';
  @Input() filters: GetAssetsInput;
  @Input() isBusy = false;
  @Input() hideCheckbox = false;
  @Input() list: ListService;
  @Input() selected: AssetDetailsDto[];
  @Input() assetColumns: { [x: string]: boolean };

  @Output() selectedChange = new EventEmitter<AssetDetailsDto[]>();
  @Output() assetColumnsChange = new EventEmitter<{ [x: string]: boolean }>();
  @Output() isBusyChange = new EventEmitter<boolean>();

  columnsMetadata: ColumnMetadata[] = [];

  window = window;
  isModalOpen = false;
  modalType = '';
  assetType = AssetType;
  pendingPageChange = false;
  SelectionType = SelectionType;
  ColumnMode = ColumnMode;
  selectedAsset: AssetDetailsDto;
  ComplianceStatus = ComplianceStatus;
  dataHistory: AssetDetailsDto[] = [];
  showRequestModal: boolean = false;
  expandedRowId: number | null = null;
  openEventsModal: boolean = false;
  appendInProgress = false;
  pageSizeOptions = [20, 50, 100];
  currentPageSize = 20;
  data: PagedResultDto<AssetDetailsDto> = {
    items: [],
    totalCount: 0,
  };
  todayPlus5Days: Date = new Date();

  private listSubscription: Subscription;
  readonly getShortDateFormat = getShortDateFormat;

  // T031: Visible columns based on user preferences
  visibleColumns: ColumnMetadata[] = [];

  // T079: Column widths map from preferences
  columnWidths: { [key: string]: number } = {};

  constructor(
    public readonly configState: ConfigStateService,
    private confirmation: ConfirmationService,
    private readonly toasterService: ToasterService,
    private router: Router,
    public readonly modelService: ModelService,
    public readonly service: AssetService,
    private columnPreferenceService: ColumnPreferenceService
  ) {}

  ngOnInit() {
    // T028-T029: Load and subscribe to column preferences
    this.columnPreferenceService.loadPreferences().then(() => {
      this.columnPreferenceService.getPreferences().pipe(take(1)).subscribe(prefs => {
        if (prefs) {
          this.columnsMetadata = Object.keys(prefs.columns).map(key => ({
            id: key,
            ...prefs.columns[key]
          }));

          // Extract ordered visible columns and widths using helper methods
          const orderedColumnIds = this.columnPreferenceService.getOrderedColumnIds(prefs);
          const columnWidths = this.columnPreferenceService.getColumnWidths(prefs);

          this.applyColumnPreferences(orderedColumnIds, columnWidths);
          // Reorder columns if datatable is already initialized
          if (this.table && this.table._internalColumns) {
            this.reorderTableColumns();
          }
        }
      });
    });

    // T032: Remove conditional maxResultCount based on activeTab
    this.list.maxResultCount = this.currentPageSize;
    this.listSubscription = this.list
      .hookToQuery(query => {
        const request: GetAssetsInput = {
          ...query,
          ...this.filters,
          secondaryID: this.filters.primaryID,
          manufacturerIdOne: this.filters.primaryID,
          manufacturerIdTwo: this.filters.primaryID,
          term: query.filter,
        };
        return this.service.getList(request);
      })
      .subscribe(data => {
        if (this.appendInProgress) {
          this.appendInProgress = false;
        }
        this.data = data;
      });
  }

  /**
   * T030: Apply column preferences to determine visible columns
   * T078: Apply column widths from preferences
   * Filters ASSET_COLUMN_METADATA based on columnOrder preference
   */
  applyColumnPreferences(columnOrder: string[], columnWidths: { [key: string]: number }): void {
    // Store column widths for use in template
    this.columnWidths = columnWidths || {};

    this.visibleColumns = columnOrder
      .map(colId => this.columnsMetadata.find(meta => meta.id === colId))
      .filter((col): col is ColumnMetadata => col !== undefined)
      .map(col => ({
        ...col,
        width: columnWidths[col.id] || col.width
      }));
  }

  /**
   * Programmatically reorder table columns based on visibleColumns order
   * Called after view init and when preferences change
   */
  ngAfterViewInit(): void {
    // Apply column order after view is initialized
    if (this.visibleColumns.length > 0) {
      setTimeout(() => this.reorderTableColumns(), 0);
    }
  }

  /**
   * Reorder the ngx-datatable columns based on user preferences
   * Uses the datatable's internal column array and reorders it
   */
  private reorderTableColumns(): void {
    if (!this.table || !this.table._internalColumns) {
      return;
    }

    const columns = [...this.table._internalColumns];

    // Create a map of column names to their preferred order
    const orderMap = new Map<string, number>();

    // Fixed columns
    orderMap.set('checkbox', 0);
    orderMap.set('image', 5);

    // Dynamic columns from preferences (offset by 10)
    this.visibleColumns.forEach((col, index) => {
      orderMap.set(col.id, index + 10);
    });

    // Expand button always last
    orderMap.set('expand', 9999);

    // Sort columns by order, keeping unmapped columns at the end
    columns.sort((a, b) => {
      const orderA = orderMap.get(this.getColumnIdentifier(a)) ?? 9998;
      const orderB = orderMap.get(this.getColumnIdentifier(b)) ?? 9998;
      return orderA - orderB;
    });

    // Update the table's internal columns
    this.table._internalColumns = columns;

    // Trigger table recalculation
    if (this.table.recalculate) {
      this.table.recalculate();
    }
  }

  /**
   * Get a unique identifier for a column
   * Used to match columns with preference order
   */
  private getColumnIdentifier(column: any): string {
    // Try to identify the column by its prop or name
    if (column.prop) {
      return column.prop;
    }
    if (column.name) {
      return column.name;
    }
    // For columns without explicit prop, try to infer from template
    if (column.frozenLeft && column.width === 110) {
      return 'primaryID';
    }
    if (column.width === 40 && column.maxWidth === 40 && !column.frozenLeft) {
      return 'expand';
    }
    if (column.width === 40 && column.frozenLeft) {
      return 'checkbox';
    }
    if (column.width === 105 && column.maxWidth === 105) {
      return 'image';
    }
    return 'unknown';
  }

  /**
   * Helper method to check if a column is visible based on preferences
   * Used in template to conditionally show columns
   */
  isColumnVisible(columnId: string): boolean {
    return this.visibleColumns.some(col => col.id === columnId);
  }

  /**
   * Get width for a column from preferences or defaults
   */
  getColumnWidth(columnId: string): number | undefined {
    const column = this.visibleColumns.find(col => col.id === columnId);
    return column?.width;
  }

  /**
   * Get the index/position of a column in the visible columns array
   * Returns -1 if column is not visible
   * Used for ordering columns in the template via CSS order property
   */
  getColumnOrder(columnId: string): number {
    const index = this.visibleColumns.findIndex(col => col.id === columnId);
    // Offset by 10 to leave room for fixed columns (checkbox=0, image=5, expand=9999)
    return index !== -1 ? index + 10 : 9999;
  }

  /**
   * Sorts column IDs based on their order in visibleColumns
   * Used to render column groups in the correct order
   */
  sortColumnIdsByPreference(columnIds: string[]): string[] {
    return columnIds
      .map(id => ({ id, order: this.getColumnOrder(id) }))
      .filter(item => item.order !== -1)
      .sort((a, b) => a.order - b.order)
      .map(item => item.id);
  }

  /**
   * Get the minimum order value from a list of column IDs
   * Used for grouping multiple logical columns in one physical column
   */
  getMinColumnOrder(columnIds: string[]): number {
    const orders = columnIds
      .map(id => this.getColumnOrder(id))
      .filter(order => order !== 9999);
    return orders.length > 0 ? Math.min(...orders) : 9999;
  }

  /**
   * T052-T053: Handle column reorder event from ngx-datatable
   * User Story 2 - Column Reordering
   */
  onColumnReorder(event: any): void {
    // Extract new column order from event
    // ngx-datatable reorder event structure: { column, newValue, prevValue }
    // For full column array, we need to reconstruct the order from visibleColumns

    if (!event || !this.visibleColumns) {
      return;
    }

    // Get the new order from current visibleColumns state
    // ngx-datatable updates its internal column order before firing the event
    const newColumnOrder = this.visibleColumns.map(col => col.id);

    // T053: Update preferences via ColumnPreferenceService
    this.columnPreferenceService.updateColumnOrder(newColumnOrder);

    // Auto-save column order (optional - can be manual save via dropdown)
    // Uncomment if auto-save is desired:
    // this.columnPreferenceService.savePreferences().catch(err => {
    //   console.error('Failed to save column order:', err);
    // });
  }

  /**
   * T076-T077: Handle column resize event from ngx-datatable
   * User Story 4 - Column Width Adjustment
   */
  onColumnResize(event: any): void {
    // ngx-datatable resize event structure: { column, newValue }
    // column.prop contains the column ID
    if (!event || !event.column || !event.column.prop) {
      return;
    }

    const columnId = event.column.prop;
    const newWidth = event.newValue;

    // T077: Update width in preferences via ColumnPreferenceService
    this.columnPreferenceService.updateColumnWidth(columnId, newWidth);

    // Update local columnWidths map for immediate UI feedback
    this.columnWidths[columnId] = newWidth;

    // Auto-save column width (optional - can be manual save via dropdown)
    // Uncomment if auto-save is desired:
    // this.columnPreferenceService.savePreferences().catch(err => {
    //   console.error('Failed to save column width:', err);
    // });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters'] && !changes['filters'].firstChange) {
      this.pendingPageChange = false;
      if (this.table) {
        this.table.offset = 0;
      }
      this.list.get();
    }
  }

  onPageChange(event: { offset: number }) {
    this.pendingPageChange = true;
    this.list.page = event.offset;
    this.list.get();
  }

  onPageSizeChange(size: number) {
    this.currentPageSize = size;
    this.list.maxResultCount = size;
    this.list.page = 0;
    if (this.table) {
      this.table.offset = 0;
    }
    this.list.get();
  }

  onAssetColumnsChange(keys: { [x: string]: boolean }) {
    this.assetColumns = keys;
    this.assetColumnsChange.emit(keys);

    // Re-apply column preferences from service after save
    const prefs = this.columnPreferenceService.getCurrentPreferences();
    if (prefs) {
      const orderedColumnIds = this.columnPreferenceService.getOrderedColumnIds(prefs);
      const columnWidths = this.columnPreferenceService.getColumnWidths(prefs);
      this.applyColumnPreferences(orderedColumnIds, columnWidths);
      if (this.table && this.table._internalColumns) {
        this.reorderTableColumns();
      }
    }
  }

  onIsBusyChange(busy: boolean) {
    this.isBusy = busy;
    this.isBusyChange.emit(busy);
  }

  onModalOpen(modalType: string, asset: AssetDetailsDto) {
    this.selectedAsset = asset;
    if (modalType === 'events') {
      this.openEventsModal = true;
    } else {
      this.modalType = modalType;
      this.isModalOpen = true;
    }
  }

  getFormattedRequestIds(row: any): string {
    return row.requestIds?.length ? row.requestIds.map((id: number) => `#${id}`).join(', ') : '-';
  }

  onModalClose(isApplied: boolean) {
    this.modalType = '';
    this.isModalOpen = false;
    isApplied && this.list.get();
  }

  formatAssetType(type: AssetType): string {
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  }

  toggleExpandRow(row) {
    this.expandedRowId = this.expandedRowId === row.id ? null : row.id;
    this.table?.rowDetail?.toggleExpandRow(row);
  }

  showMore() {
    if (this.isBusy || this.appendInProgress || !this.hasMore()) return;
    this.appendInProgress = true;
    const skip = this.data.items.length;
    const take = this.list.maxResultCount || 10;
    const request: GetAssetsInput = {
      ...this.filters,
      secondaryID: this.filters.primaryID,
      manufacturerIdOne: this.filters.primaryID,
      manufacturerIdTwo: this.filters.primaryID,
      skipCount: skip,
      maxResultCount: take,
    } as GetAssetsInput;

    this.service.getList(request).subscribe(next => {
      const wasAllSelectedBefore = this.isAllRowsSelected();
      const previousItems = this.data.items;
      const merged = [...previousItems, ...next.items].filter(
        (asset, index, self) => index === self.findIndex(a => a.id === asset.id)
      );
      this.data = { items: merged, totalCount: next.totalCount };
      this.appendInProgress = false;
      if (!this.hideCheckbox && wasAllSelectedBefore) {
        this.selectedChange.emit([...merged]);
      }
    });
  }

  hasMore(): boolean {
    return (this.data?.items?.length || 0) < (this.data?.totalCount || 0);
  }

  onView(record: AssetDetailsDto) {
    this.router.navigate(['/assets/view', record.id]);
  }

  onInspect(record: AssetDetailsDto) {
    this.router.navigate(['/inspections/create'], { queryParams: { assetId: record.id } });
  }

  update(record: AssetDetailsDto) {
    this.router.navigate(['/assets/edit', record.id]);
  }

  onSelectAssets(event: any, item: AssetDetailsDto) {
    const index = this.selected.findIndex(subAsset => subAsset.id === item.id);
    const items = [...this.selected];
    if (event.target.checked) {
      if (index === -1) {
        items.push(item);
      }
    } else {
      if (index !== -1) {
        items.splice(index, 1);
      }
    }
    this.selectedChange.emit([...items]);
  }

  isSelected(row: AssetDetailsDto): boolean {
    return this.selected.some(e => e.id === row.id);
  }

  isAllRowsSelected(): boolean {
    if (!this.data.items.length || !this.selected.length) {
      return false;
    }
    const allSelected = this.data.items.every(item =>
      this.selected.some(selectedItem => selectedItem.id === item.id)
    );
    return allSelected;
  }

  formatAvailability(value: string): string {
    if (!value) return '';
    return value
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }

  selectAllRows(select: boolean): void {
    if (select) {
      const allItems = this.data.items.filter(
        item => !this.selected.some(selectedItem => selectedItem.id === item.id)
      );
      this.selectedChange.emit([...this.selected, ...allItems]);
    } else {
      this.selectedChange.emit(
        this.selected.filter(
          selectedItem => !this.data.items.some(item => item.id === selectedItem.id)
        )
      );
    }
  }

  onSelectRow({ selected }: any): void {
    if (this.pendingPageChange) return;
    const items = [...selected].filter(
      (asset, index, self) => index === self.findIndex(a => a.id === asset.id)
    );

    this.selectedChange.emit([...items]);
  }

  clearSelected() {
    this.selectedChange.emit([]);
  }

  delete(record: AssetDetailsDto) {
    this.confirmation
      .warn('RegisterService::DeleteConfirmationMessage', 'RegisterService::AreYouSure', {
        messageLocalizationParams: [],
      })
      .pipe(
        filter(status => status === Confirmation.Status.confirm),
        switchMap(() => this.service.delete(record.id))
      )
      .subscribe(this.list.get);
  }

  collectAsset(asset: AssetDetailsDto) {
    this.service.collectAsset(asset).subscribe({
      next: () => {
        this.toasterService.success('RegisterService::Successful');
        this.list.get();
      },
    });
  }

  offHireAsset(asset: AssetDetailsDto) {
    this.service.offHire(asset).subscribe({
      next: () => {
        this.toasterService.success('RegisterService::Successful');
        this.list.get();
      },
    });
  }

  ngOnDestroy() {
    this.listSubscription?.unsubscribe();
  }
}
